import {
  isInjectedWebVitalsStartTimeError,
  isNuxtBuildMetaFetchError,
} from '~/utils/ignoredMonitoringErrors';

type ClientMonitorPayload = {
  source: string;
  level?: 'warning' | 'error' | 'critical';
  message: string;
  description?: string | null;
  stack?: string | null;
  status?: number | null;
  exception_class?: string | null;
  url?: string | null;
  route?: string | null;
  component?: string | null;
  file?: string | null;
  line?: string | number | null;
  user_agent?: string | null;
  user_id?: string | number | null;
  user_email?: string | null;
  user_name?: string | null;
  context?: Record<string, unknown>;
};

const SECRET_KEY_PATTERN =
  /(authorization|cookie|password|password_confirmation|secret|token|api[_-]?key|client[_-]?secret|access[_-]?token|refresh[_-]?token|credential|session|private[_-]?key|card|cvv)/i;

const seen = new Map<string, number>();
const CLIENT_COOLDOWN_MS = 5 * 60 * 1000;
const KNOWN_PROBE_PATH_PREFIXES = [
  '/.env',
  '/.git',
  '/wp-',
  '/wordpress',
  '/vendor/phpunit',
  '/phpinfo',
  '/config.',
  '/actuator',
  '/server-status',
];

const scrubString = (value: string): string =>
  value
    .replace(/Bearer\s+[A-Za-z0-9\-._~+/]+=*/gi, 'Bearer [REDACTED]')
    .replace(/Basic\s+[A-Za-z0-9\-._~+/]+=*/gi, 'Basic [REDACTED]')
    .replace(
      /((?:authorization|password|secret|token|api[_-]?key|client[_-]?secret|access[_-]?token|refresh[_-]?token|credential)=)([^&\s]+)/gi,
      '$1[REDACTED]',
    )
    .slice(0, 5000);

export const scrubMonitoringValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(scrubMonitoringValue);
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, item]) => [
        key,
        SECRET_KEY_PATTERN.test(key)
          ? '[REDACTED]'
          : scrubMonitoringValue(item),
      ]),
    );
  }

  return typeof value === 'string' ? scrubString(value) : value;
};

const toError = (value: unknown): Error => {
  if (value instanceof Error) return value;
  if (typeof value === 'string') return new Error(value);

  try {
    return new Error(JSON.stringify(scrubMonitoringValue(value)));
  } catch {
    return new Error(String(value));
  }
};

const numberOrNull = (value: unknown): number | null => {
  const numberValue = Number(value);

  return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : null;
};

const statusFromError = (value: unknown): number | null => {
  const data = value as Record<string, any> | null;

  return numberOrNull(
    data?.statusCode ??
      data?.status ??
      data?.response?.status ??
      data?.response?.statusCode ??
      data?.data?.statusCode,
  );
};

const pathFromUrlOrRoute = (value: unknown): string | null => {
  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }

  const raw = value.trim();

  try {
    return new URL(raw, window.location.origin).pathname;
  } catch {
    return raw.split(/[?#]/)[0] || null;
  }
};

const appendQueryParams = (target: URL, value: unknown): void => {
  if (!value || typeof value !== 'object') {
    return;
  }

  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    if (item === undefined || item === null || item === '') {
      continue;
    }

    const values = Array.isArray(item) ? item : [item];

    for (const queryValue of values) {
      if (queryValue === undefined || queryValue === null) {
        continue;
      }

      target.searchParams.append(
        key,
        typeof queryValue === 'object'
          ? JSON.stringify(queryValue)
          : String(queryValue),
      );
    }
  }
};

const resolveApiRequestUrl = (
  url: string,
  context: Record<string, unknown>,
): string => {
  if (typeof window === 'undefined') {
    return url;
  }

  try {
    const baseURL =
      typeof context.baseURL === 'string' && context.baseURL.trim() !== ''
        ? context.baseURL.trim()
        : '/api';
    const normalizedBaseURL = baseURL.endsWith('/') ? baseURL : `${baseURL}/`;
    const target = /^https?:\/\//i.test(url)
      ? new URL(url)
      : new URL(
          url.replace(/^\/+/, ''),
          new URL(normalizedBaseURL, window.location.origin),
        );

    appendQueryParams(target, context.query ?? context.params);

    return target.toString();
  } catch {
    return url;
  }
};

const pageNotFoundPath = (message: string): string | null => {
  const match = message.match(/^Page not found:\s*(\S+)/i);

  return match ? pathFromUrlOrRoute(match[1]) : null;
};

const isKnownProbePath = (value: unknown): boolean => {
  const path = pathFromUrlOrRoute(value)?.toLowerCase();
  if (!path) return false;

  return KNOWN_PROBE_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
};

const shouldIgnoreClientMonitoringError = (
  source: string,
  message: string,
  status: number | null,
  route: string,
  url: string,
): boolean => {
  const notFoundProbePath = pageNotFoundPath(message);

  if (
    [route, url, notFoundProbePath].some((value) => isKnownProbePath(value))
  ) {
    return true;
  }

  if (source === 'nuxt.app.error' && /^Page not found:/i.test(message)) {
    return true;
  }

  return status !== null && status >= 400 && status < 500;
};

const scalarAccountField = (
  account: Record<string, unknown> | null,
  key: string,
): string | number | null => {
  const value = account?.[key];

  return typeof value === 'string' || typeof value === 'number' ? value : null;
};

const stringAccountField = (
  account: Record<string, unknown> | null,
  key: string,
): string | null => {
  const value = account?.[key];

  return typeof value === 'string' && value !== '' ? value : null;
};

const describeClientMonitoringError = (
  source: string,
  message: string,
  status: number | null,
  route: string,
): string => {
  if (source === 'nuxt.app.error' && /^Page not found:/i.test(message)) {
    return `Nuxt could not match the requested route (${route}). This is usually a direct URL/navigation 404, not a form workflow crash.`;
  }

  if (status !== null && status >= 500) {
    return `The browser reported an HTTP ${status} failure while the user was on ${route}.`;
  }

  if (source === 'vue.error') {
    return `Vue reported a component/runtime error while rendering or updating ${route}.`;
  }

  if (source.startsWith('window.')) {
    return `The browser reported an uncaught runtime error while the user was on ${route}.`;
  }

  return `Nuxt reported a client-side runtime error while the user was on ${route}.`;
};

const stringifyLocalizedValue = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim() !== '') {
    return value;
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;

    return (
      stringifyLocalizedValue(record.en) ?? stringifyLocalizedValue(record.ar)
    );
  }

  return null;
};

const resolveClientAccount = (): Record<string, unknown> | null => {
  try {
    const auth = useAuth();

    if (auth.status?.value && auth.status.value !== 'authenticated') {
      return null;
    }

    const sessionUser = (auth.data?.value as any)?.user;
    const userData = sessionUser?.data ?? sessionUser ?? {};
    const contact = userData?.contact ?? {};
    const company = contact?.company ?? userData?.company ?? {};
    const name =
      stringifyLocalizedValue(userData?.name) ??
      [
        stringifyLocalizedValue(contact?.name?.en?.first) ??
          stringifyLocalizedValue(contact?.name?.ar?.first),
        stringifyLocalizedValue(contact?.name?.en?.third) ??
          stringifyLocalizedValue(contact?.name?.ar?.third),
      ]
        .filter(Boolean)
        .join(' ')
        .trim();

    const account = {
      id: userData?.userId ?? userData?.id ?? sessionUser?.id ?? null,
      email: userData?.email ?? sessionUser?.email ?? null,
      name: name || null,
      contactId:
        contact?.contactId ?? contact?.id ?? userData?.contactId ?? null,
      companyId:
        contact?.company_id ??
        contact?.companyId ??
        company?.id ??
        userData?.company_id ??
        userData?.companyId ??
        null,
      company: stringifyLocalizedValue(company?.name),
    };

    return Object.fromEntries(
      Object.entries(account).filter(
        ([, value]) => value !== null && value !== '',
      ),
    );
  } catch {
    return null;
  }
};

const buildClientContext = (
  context: Record<string, unknown>,
  account: Record<string, unknown> | null,
): Record<string, unknown> => {
  return scrubMonitoringValue({
    ...context,
    account,
  }) as Record<string, unknown>;
};

const shouldSend = (payload: ClientMonitorPayload): boolean => {
  const context = payload.context ?? {};
  const key = [
    payload.source,
    payload.status,
    payload.message,
    payload.file,
    payload.line,
    typeof context.apiUrl === 'string' ? context.apiUrl : '',
    typeof window !== 'undefined' ? window.location.pathname : '',
  ].join('|');
  const now = Date.now();
  const lastSeen = seen.get(key) ?? 0;

  if (now - lastSeen < CLIENT_COOLDOWN_MS) {
    return false;
  }

  seen.set(key, now);
  return true;
};

const sendClientMonitoringPayload = async (
  payload: ClientMonitorPayload,
): Promise<void> => {
  if (!shouldSend(payload)) {
    return;
  }

  try {
    await $fetch('/api/client-errors', {
      method: 'POST',
      body: payload,
      credentials: 'include',
    });
  } catch {
    // Monitoring must never alter user-facing application behavior.
  }
};

export const reportClientMonitoringError = async (
  error: unknown,
  source: string,
  context: Record<string, unknown> = {},
): Promise<void> => {
  if (typeof window === 'undefined') {
    return;
  }

  if (isInjectedWebVitalsStartTimeError(error, context)) {
    return;
  }

  const normalized = toError(error);
  const status = statusFromError(error);
  const route = window.location.pathname;
  const url = window.location.href;
  const message = scrubString(normalized.message || source);

  if (
    shouldIgnoreClientMonitoringError(source, message, status, route, url) ||
    isNuxtBuildMetaFetchError(error, context)
  ) {
    return;
  }

  const account = resolveClientAccount();
  const description = scrubString(
    describeClientMonitoringError(source, message, status, route),
  );
  const payload: ClientMonitorPayload = {
    source,
    level: 'error',
    message,
    description,
    stack: normalized.stack ? scrubString(normalized.stack) : null,
    status,
    exception_class: normalized.name || null,
    url: scrubString(url),
    route,
    user_agent: scrubString(navigator.userAgent),
    user_id: scalarAccountField(account, 'id'),
    user_email: stringAccountField(account, 'email'),
    user_name: stringAccountField(account, 'name'),
    context: buildClientContext({ ...context, description }, account),
  };

  await sendClientMonitoringPayload(payload);
};

const getResponseMessage = (value: unknown): string | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const data = value as Record<string, any>;
  const message =
    data?.data?.message ??
    data?.message ??
    data?.error ??
    data?.statusMessage ??
    null;

  return typeof message === 'string' ? message : null;
};

export const reportClientApiNetworkFailure = async (
  url: string,
  error: unknown,
  context: Record<string, unknown> = {},
): Promise<void> => {
  if (typeof window === 'undefined') {
    return;
  }

  const response = (error as any)?.response ?? error;
  const statusValue = response?.status ?? response?.statusCode;
  const status = Number(statusValue);
  const hasHttpStatus = Number.isFinite(status) && status > 0;
  const shouldReportHttpFailure = hasHttpStatus && status >= 500;
  const shouldReportNetworkFailure = !hasHttpStatus;

  if (!shouldReportHttpFailure && !shouldReportNetworkFailure) {
    return;
  }

  const responseData = response?._data ?? response?.data ?? null;
  const responseMessage = getResponseMessage(responseData);
  const statusText = response?.statusText ?? response?.statusMessage ?? null;
  const normalized = toError(error);
  const apiRequestUrl = resolveApiRequestUrl(url, context);
  const fallbackMessage = shouldReportHttpFailure
    ? `API request failed with HTTP ${status}: ${url}`
    : `API network failure: ${url}`;
  const account = resolveClientAccount();
  const description = scrubString(
    shouldReportHttpFailure
      ? `The browser received HTTP ${status} from a Nuxt/API request while the user was on ${window.location.pathname}.`
      : `The browser could not reach a Nuxt/API endpoint while the user was on ${window.location.pathname}.`,
  );

  const payload: ClientMonitorPayload = {
    source: shouldReportHttpFailure ? 'nuxt.api.http' : 'nuxt.api.network',
    level: shouldReportHttpFailure && status >= 503 ? 'critical' : 'error',
    message: scrubString(
      responseMessage ||
        (typeof statusText === 'string' && statusText !== ''
          ? statusText
          : normalized.message || fallbackMessage),
    ),
    description,
    stack: normalized.stack ? scrubString(normalized.stack) : null,
    status: shouldReportHttpFailure ? status : null,
    exception_class: normalized.name || null,
    url: scrubString(apiRequestUrl),
    route: window.location.pathname,
    user_agent: scrubString(navigator.userAgent),
    user_id: scalarAccountField(account, 'id'),
    user_email: stringAccountField(account, 'email'),
    user_name: stringAccountField(account, 'name'),
    context: scrubMonitoringValue({
      ...context,
      description,
      account,
      apiUrl: url,
      apiRequestUrl,
      pageUrl: window.location.href,
      apiResponseUrl: response?.url ?? null,
      status: shouldReportHttpFailure ? status : null,
      statusText,
      browserOnline:
        typeof navigator.onLine === 'boolean' ? navigator.onLine : null,
      response: responseData,
    }) as Record<string, unknown>,
  };

  await sendClientMonitoringPayload(payload);
};
